import * as amplitude from '@amplitude/analytics-browser';
import * as Sentry from '@sentry/react';
import _ from 'lodash';
import { getCookie, getQueryParam, isDev, setCookie } from './clientService';
import {
  getLocalStorageItem,
  LOCAL_STORAGE_IGNORE_ANALYTICS,
} from './localStorageService';
import { postRequest } from './requestService';
export const GOOGLE_ADS_ACCOUNT_ID = "AW-16890086783"
export const GOOGLE_ADS_CONVERSION_PURCHASE = "T8BPCLr5rOUaEP-K6fU-"
export const GOOGLE_ADS_CONVERSION_SIGNUP = "-3PNCPfrtbYaEP-K6fU-"

const isDevMode = isDev();
const AMPLITUDE_KEY = "866979ee4cec50f80455a502c146cfda";
const META_PIXEL_ID = "669014956056059";

// Helper: Get UTM params
const getUtmParamsFromUrl = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const utmParams = {};

  for (const [key, value] of urlParams.entries()) {
    if (key.startsWith('utm_')) {
      utmParams[key] = value;
    }
  }
  return utmParams;
};

// Initialize Analytics
export const initAnalytics = () => {
  if (isDevMode || getLocalStorageItem(LOCAL_STORAGE_IGNORE_ANALYTICS)) return;

  // Initialize Sentry
  Sentry.init({
    dsn: "https://5e1eb74ae6c647361732a9cff506929c@o4509168761438208.ingest.us.sentry.io/4509168763535360",
    integrations: [
      Sentry.replayIntegration({
        maskAllText: false,
        maskAllInputs: false,
        blockAllMedia: false,
      }),
    ],
    whitelistUrls: ['getingenie.com', 'www.getingenie.com', 'app.getingenie.com'],
    replaysSessionSampleRate: 1.0,
    replaysOnErrorSampleRate: 1.0,
    cookieOptions: {
      domain: isDevMode ? 'localhost' : '.getingenie.com',
    },
  });

  // Initialize Meta Pixel
  if (!window.fbq) {
    window.fbq = function () {
      window.fbq.callMethod ?
        window.fbq.callMethod.apply(window.fbq, arguments) :
        window.fbq.queue.push(arguments);
    };
    window._fbq = window.fbq;
    window.fbq.push = window.fbq;
    window.fbq.loaded = true;
    window.fbq.version = '2.0';
    window.fbq.queue = [];

    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://connect.facebook.net/en_US/fbevents.js';
    document.getElementsByTagName('head')[0].appendChild(script);

    window.fbq('init', META_PIXEL_ID);
    window.fbq('track', 'PageView');
  }

  // Initialize Amplitude
  if (AMPLITUDE_KEY) {
    const urlParams = new URLSearchParams(window.location.search);
    const passedDeviceId = urlParams.get('amp_device_id');

    amplitude.init(AMPLITUDE_KEY, {
      deviceIdFromUrlParam: true,
      deviceId: passedDeviceId || undefined,
      cookieOptions: {
        domain: isDevMode ? 'localhost' : '.getingenie.com',
      },
      defaultTracking: {
        sessions: true,
        pageViews: false,
        fileDownloads: false,
        formInteractions: false,

      },
      trackingOptions: {
        ipAddress: true,
        language: true,
        platform: true,
        region: true,
        version: true,
      },
      logLevel: amplitude.Types.LogLevel.Warn,
    });

    const utmParams = getUtmParamsFromUrl();
    if (Object.keys(utmParams).length > 0) {
      const identify = new amplitude.Identify();
      Object.entries(utmParams).forEach(([key, value]) => {
        identify.set(key, value);
      });
      amplitude.identify(identify);
    }
  }
};

// Report a custom event
export const reportEvent = (category, action, additionalParams = {}) => {
  // if (isDevMode) return;

  if (window.gtag) {
    window.gtag('event', action, {
      event_category: category,
      event_label: window.location.href,
      ...additionalParams,
    });
  }
  if (AMPLITUDE_KEY) {
    amplitude.track(`${category}_${action}`, additionalParams);
  }
};

// Report a screen view
export const reportScreen = (screenName) => {
  // if (isDevMode) return;

  if (AMPLITUDE_KEY) {
    amplitude.track('user_pageview', { screenName, platform: 'app' });
  }

  if (window.gtag) {
    window.gtag('event', 'page_view', {
      page_title: screenName,
      page_location: window.location.href,
      page_path: window.location.pathname + window.location.search,
    });
  }
};

/**
 * Update user in Amplitude, Sentry, and Inspecgtlet
 */
export const setUserProperty = (userDetails, key, value) => {
  if (isDevMode || !userDetails._id) return;

  if (window?.__insp) {
    window.__insp.push(['identify', userDetails._id]);
    window.__insp.push([
      'tagSession',
      {
        [key]: value,
      },
    ]);
  }

  Sentry.setContext('user', {
    id: userDetails._id,
    [key]: value,
  });
  Sentry.setUser({
    id: userDetails._id,
    [key]: value,
  });

  if (AMPLITUDE_KEY) {
    amplitude.setUserId(userDetails._id);
    const identify = new amplitude.Identify()
      .set(key, value)
    amplitude.identify(identify);
  }


};

// Set full user details
export const setUserProperties = (userDetails) => {
  if (isDevMode || !userDetails?._id) return;

  // Inspectlet
  if (window?.__insp) {
    window.__insp.push(['identify', userDetails._id]);
    window.__insp.push([
      'tagSession',
      {
        email: userDetails.email,
        name: `${userDetails.firstName || ''} ${userDetails.lastName || ''}`.trim(),
        hasActiveSubscription: !!userDetails.subscription?.active,
      },
    ]);
    window.__insp.push([
      'tagSession',
      _.pick(userDetails?.subscription || {}, [
        'providerCustomerId',
        'billingStartDate',
        'testMode',
        'totalCharged',
        'planId',
      ]),
    ]);
  }

  // Sentry
  try {
    Sentry.setContext('user', {
      id: userDetails._id,
      email: userDetails.email,
      name: `${userDetails.firstName} ${userDetails.lastName}`,
    });
    Sentry.setUser({
      id: userDetails._id,
      email: userDetails.email,
      name: `${userDetails.firstName} ${userDetails.lastName}`,
    });
  } catch (e) {
    console.error('Failed to set Sentry user context:', e);
  }

  // Amplitude
  if (AMPLITUDE_KEY) {
    amplitude.setUserId(userDetails._id);
    const identify = new amplitude.Identify()
      .set('email', userDetails.email)
      .set('firstName', userDetails.firstName)
      .set('lastName', userDetails.lastName)
      .set('hasActiveSubscription', !!userDetails.subscription?.active);

    amplitude.identify(identify);
  }
};

// Report Google Conversion
export const reportGoogleConversion = async ({ sendTo = '', value, transactionId, kind = 'purchase' }) => {
  if (isDevMode) return;
  if (!window.gtag) return console.warn('Missing gtag script');

  window.gtag('event', 'conversion', {
    send_to: sendTo,
    value,
    currency: 'USD',
    transaction_id: transactionId,
  });

  if (kind === 'signup') {
    window.fbq('track', 'CompleteRegistration', {
      value,
      currency: 'USD',
      status: 'success',
    });
  }
};

// UTM Tracking Helpers
export const saveTrackingParams = () => {
  const trackingParams = Object.fromEntries(new URLSearchParams(window.location.search));
  if (!localStorage.getItem('trackingParams') && Object.keys(trackingParams).length > 0) {
    localStorage.setItem('trackingParams', JSON.stringify(trackingParams));
  }
};

export const getTrackingParams = () => {
  const item = localStorage.getItem('trackingParams');
  return item ? JSON.parse(item) : null;
};

export const saveJobId = () => {
  if (getCookie('job_id')) return;
  
  if (!getQueryParam('job_id')) return;

  setCookie('job_id', getQueryParam('job_id'), 30)
}

export const trackServerEvent = async (eventName) => {
  try {
    await postRequest('analytics/track-event', {event: eventName})
  }
  catch(e) {
    console.error(e)
  }
}

export const saveTestModeIfExists = () => {
  const mode = getQueryParam('mode');
  if (mode) {
    setCookie('mode', mode, 30)
  }
}